import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactFlow, { useNodesState, useEdgesState, addEdge, Controls, Background, MarkerType, ConnectionLineType } from 'reactflow';
import RoundedStepEdge from './RoundedStepEdge';
import 'reactflow/dist/style.css';
import './index.css';
import { nodeTypes } from './nodeTypes';
import api from './api';
import { useDispatch, useSelector } from 'react-redux';
import { setVars, setTags, setLocalVars, setCodes, setToastMessage } from './redux/FlowReducer';
import { Button, Dialog, DialogTitle, DialogContent, IconButton, Box, Stack, Menu, MenuItem, Divider, Snackbar, Alert } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FunctionsIcon from '@mui/icons-material/Functions';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import LoopIcon from '@mui/icons-material/Loop';
import RepeatIcon from '@mui/icons-material/Repeat';
import BuildIcon from '@mui/icons-material/Build';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import JoinLeftIcon from '@mui/icons-material/JoinLeft';
import JoinRightIcon from '@mui/icons-material/JoinRight';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import DifferenceIcon from '@mui/icons-material/Difference';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { PanelWrapper } from './PropertyPanel/PanelManager';
import CloseIcon from '@mui/icons-material/Close';
import WorkflowsDialog from './WorkflowsDialog';
import WorkflowNameDialog from './WorkflowNameDialog';
import LocalVarsDialog from './LocalVarsDialog';

const initialNodes = [
  { id: 'start', type: 'circularNode', data: { label: 'Start' }, position: { x: 120, y: 80 } },
];

const FlowToolbar = ({ onOpen, onSave, onNew, onLocalVars, name }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid #eee', bgcolor: '#f8f4ff' }}>
    <Stack direction="row" spacing={1}>
      <Button variant="contained" color="primary" onClick={onOpen}>Çalışma Akışı Aç</Button>
      <Button variant="outlined" onClick={onSave}>Kaydet</Button>
      <Button variant="text" onClick={onNew}>Yeni</Button>
      <Button variant="text" onClick={onLocalVars}>Yerel Değişkenler</Button>
    </Stack>
    <Box sx={{ ml: 'auto', color: '#666', fontSize: 14 }}>Aktif: {name || '—'}</Box>
  </Box>
);

const Toolbox = ({ items = [] }) => {
  const onDragStart = (event, item) => {
    // Only serialize safe fields (avoid icon/react elements)
    const payload = { type: item.type, label: item.label };
    event.dataTransfer.setData('application/reactflow', JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
  };
  return (
    <aside>
      <div className="description">Araç Kutusu</div>
      {items.map((it) => (
        <div key={it.key || it.type}
             className="dndnode"
             draggable
             onDragStart={(e) => onDragStart(e, it)}>
          {it.icon ? it.icon : null}
          <span style={{ marginLeft: 8 }}>{it.label}</span>
        </div>
      ))}
    </aside>
  );
};

const DnDFlow = () => {
  const dispatch = useDispatch();
  const toastMessage = useSelector((state) => state.flow.toastMessage);
  const [loadedName, setLoadedName] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [nameDlgOpen, setNameDlgOpen] = useState(false);
  const [localsOpen, setLocalsOpen] = useState(false);
  const rfRef = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const location = useLocation();
  const [selectedNode, setSelectedNode] = useState(null);
  const [propOpen, setPropOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, node: null });
  const edgeTypes = useMemo(() => ({ roundedstep: RoundedStepEdge }), []);
  const toolboxItems = useMemo(() => ([
    { type: 'circularNode', label: 'Start', key: 'start-btn', icon: <PlayArrowIcon fontSize="small" /> },
    { type: 'circularNode', label: 'Stop', key: 'stop-btn', icon: <StopIcon fontSize="small" /> },
    { type: 'Condition', label: 'Koşul', icon: <SwitchLeftIcon fontSize="small" /> },
    { type: 'Arithmetic', label: 'Aritmetik', icon: <FunctionsIcon fontSize="small" /> },
    { type: 'Switch', label: 'Switch', icon: <AltRouteIcon fontSize="small" /> },
    { type: 'Loop', label: 'Döngü', icon: <LoopIcon fontSize="small" /> },
    { type: 'For', label: 'For', icon: <RepeatIcon fontSize="small" /> },
    { type: 'Service', label: 'Servis', icon: <BuildIcon fontSize="small" /> },
    { type: 'ProgramCall', label: 'Program Çağır', icon: <IntegrationInstructionsIcon fontSize="small" /> },
    { type: 'MetinselIslemler', label: 'Metin İşlemleri', icon: <TextFieldsIcon fontSize="small" /> },
    { type: 'Email', label: 'E-Posta', icon: <EmailIcon fontSize="small" /> },
    { type: 'SMS', label: 'SMS', icon: <SmsIcon fontSize="small" /> },
    { type: 'PLCCommand', label: 'PLC Komutu', icon: <ElectricBoltIcon fontSize="small" /> },
    { type: 'Alarm', label: 'Alarm', icon: <WarningAmberIcon fontSize="small" /> },
    { type: 'And', label: 'AND', icon: <JoinLeftIcon fontSize="small" /> },
    { type: 'Or', label: 'OR', icon: <JoinRightIcon fontSize="small" /> },
    { type: 'Not', label: 'NOT', icon: <NotInterestedIcon fontSize="small" /> },
    { type: 'Xor', label: 'XOR', icon: <DifferenceIcon fontSize="small" /> },
    { type: 'Eq', label: 'Eşitlik', icon: <DragIndicatorIcon fontSize="small" /> },
  ]), []);

  // Load variables and codes for selectors
  useEffect(() => {
    api.getVariables().then((all) => {
      const vars = all.filter(v => v.type === 'V');
      const tags = all.filter(v => v.type === 'T');
      dispatch(setVars(vars));
      dispatch(setTags(tags));
    }).catch(console.error);

    api.getCodes().then((codes) => {
      dispatch(setCodes(codes || []));
    }).catch(console.error);
  }, [dispatch]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) => {
        // Condition node'dan gelen bağlantılar için renk belirle
        let strokeColor = 'var(--brand-primary)';
        const sourceNode = nodes.find(n => n.id === params.source);

        if (sourceNode?.type === 'Condition') {
          if (params.sourceHandle === 'true') {
            strokeColor = '#4CAF50'; // Yeşil - True
          } else if (params.sourceHandle === 'false') {
            strokeColor = '#f44336'; // Kırmızı - False
          }
        }

        return addEdge(
          {
            ...params,
            type: 'roundedstep',
            markerEnd: { type: MarkerType.ArrowClosed, color: strokeColor },
            style: { stroke: strokeColor, strokeWidth: 2 },
          },
          eds
        );
      }),
    [setEdges, nodes]
  );

  const openWorkflow = useCallback(async (name) => {
    const data = await api.getWorkflow(name);
    // Try to pick JSON content
    const jsonStr = data?.json || data?.Json || data?.content || data?.Content || data?.workflowJson || data?.WorkflowJson;
    let parsed = null;
    try { parsed = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr; } catch { parsed = null; }
    const flow = parsed && (parsed.nodes || parsed.edges) ? parsed : data;
    const n = Array.isArray(flow.nodes) ? flow.nodes : initialNodes;
    const e = Array.isArray(flow.edges) ? flow.edges : [];

    // Load local variables from workflow
    const localVars = (flow.localVariables || flow.LocalVariables || []).map(lv => ({
      ...lv,
      type: 'LV',
      id: `lv_${lv.id || lv.Id || lv.name}`,
      name: lv.name || lv.Name,
      displayName: lv.name || lv.Name
    }));
    dispatch(setLocalVars(localVars));

    setNodes(n);
    setEdges(e);
    setLoadedName(name);

    // Fit view after nodes are rendered
    setTimeout(() => {
      reactFlowInstance?.fitView?.({ padding: 0.2 });
    }, 100);
  }, [setNodes, setEdges, dispatch, reactFlowInstance]);

  // Auto-load when route is /flow/:name
  useEffect(() => {
    const parts = location.pathname.split('/');
    const idx = parts.findIndex(p => p === 'flow');
    const name = idx >= 0 ? parts[idx + 1] : null;
    if (name) openWorkflow(decodeURIComponent(name));
  }, [location, openWorkflow]);

  const handleSave = useCallback(async () => {
    const payload = { nodes, edges };
    if (!loadedName) { setNameDlgOpen(true); return; }
    await api.saveWorkflow({ name: loadedName, json: payload });
    dispatch(setToastMessage({ type: 'success', message: 'Workflow kaydedildi.' }));
  }, [nodes, edges, loadedName, dispatch]);

  const handleNew = useCallback(() => {
    setNodes(initialNodes);
    setEdges([]);
    setLoadedName('');
  }, [setNodes, setEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const bounds = rfRef.current?.getBoundingClientRect();
    let payload = null;
    try { payload = JSON.parse(event.dataTransfer.getData('application/reactflow')); } catch {}
    if (!payload?.type || !bounds) return;
    const pt = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    const position = reactFlowInstance?.screenToFlowPosition ? reactFlowInstance.screenToFlowPosition(pt) : pt;
    const id = `${payload.type}-${Date.now()}`;
    let data = { label: payload.label || payload.type };
    if (payload.type === 'circularNode') {
      // Auto convert to Stop if Start already exists
      const hasStart = nodes.some(n => n.type === 'circularNode' && (n.data?.label === 'Start'));
      const hasStop = nodes.some(n => n.type === 'circularNode' && (n.data?.label === 'Stop' || n.data?.label === 'End'));
      if (payload.label === 'Stop') {
        data = { label: 'Stop' };
      } else if (!hasStart) {
        data = { label: 'Start' };
      } else if (!hasStop) {
        data = { label: 'Stop' };
      } else {
        data = { label: 'Start' };
      }
    }
    const newNode = { id, type: payload.type, position, data };
    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes, nodes]);

  const openProperties = (node) => { setSelectedNode(node); setPropOpen(true); };
  const deleteNode = (node) => setNodes((nds) => nds.filter((n) => n.id !== node.id));
  const duplicateNode = (node) => setNodes((nds) => nds.concat({ ...node, id: `${node.id}-${Date.now()}`, position: { x: node.position.x + 30, y: node.position.y + 30 } }));
  const setStartStop = (node, label) => setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, label, Title: label } } : n));

  // Delete tuşu ile seçili node'ları sil
  const onNodesDelete = useCallback((deletedNodes) => {
    setNodes((nds) => nds.filter((n) => !deletedNodes.find((d) => d.id === n.id)));
  }, [setNodes]);

  // Keyboard handler - Delete/Backspace tuşları
  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Delete' || event.key === 'Backspace') && !propOpen) {
      const selectedNodes = nodes.filter((n) => n.selected);
      if (selectedNodes.length > 0) {
        setNodes((nds) => nds.filter((n) => !n.selected));
        // Seçili edge'leri de sil
        setEdges((eds) => eds.filter((e) => !e.selected));
      }
    }
  }, [nodes, setNodes, setEdges, propOpen]);

  return (
    <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <FlowToolbar
        onOpen={async () => {
          try {
            const list = await api.listWorkflows();
            setWorkflows(list || []);
            setPickerOpen(true);
          } catch (e) {
            console.error('Workflow list error:', e);
            const msg = e?.response?.data?.message || e?.message || 'Workflow listesi alınamadı';
            window.alert(msg);
          }
        }}
        onSave={handleSave}
        onNew={() => { setLoadedName(''); setNameDlgOpen(true); }}
        onLocalVars={() => setLocalsOpen(true)}
        name={loadedName}
      />
      <div className="dndflow" style={{ display: 'flex', flex: 1 }}>
        <Toolbox items={toolboxItems} />
        <div className="reactflow-wrapper" ref={rfRef}>
          <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDoubleClick={(e, n) => { openProperties(n); }}
          onNodeContextMenu={(e, n) => { e.preventDefault(); setCtxMenu({ open: true, x: e.clientX, y: e.clientY, node: n }); }}
          onPaneContextMenu={(e) => { e.preventDefault(); setCtxMenu({ open: true, x: e.clientX, y: e.clientY, node: null }); }}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: 'roundedstep', markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: 'var(--brand-primary)', strokeWidth: 2 } }}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={{ stroke: 'var(--brand-primary)', strokeWidth: 2 }}
          deleteKeyCode={propOpen ? null : ['Delete', 'Backspace']}
          fitView
        >
          <Controls />
          <Background color={getComputedStyle(document.documentElement).getPropertyValue('--grid-color') || '#e9defa'} gap={20} />
          </ReactFlow>
        </div>
      </div>
      <WorkflowsDialog open={pickerOpen} onClose={() => setPickerOpen(false)} workflows={workflows} onWorkflowsChange={setWorkflows} onSelect={(name) => { setPickerOpen(false); openWorkflow(name); }} />
      <WorkflowNameDialog open={nameDlgOpen} onClose={() => setNameDlgOpen(false)} onSave={(n) => { setLoadedName(n); setNameDlgOpen(false); }} initialName={loadedName} />
      <LocalVarsDialog open={localsOpen} onClose={() => setLocalsOpen(false)} />
      <Menu open={ctxMenu.open} onClose={() => setCtxMenu({ ...ctxMenu, open: false })} anchorReference="anchorPosition" anchorPosition={ctxMenu.open ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}>
        {ctxMenu.node ? [
          <MenuItem key="props" onClick={() => { openProperties(ctxMenu.node); setCtxMenu({ ...ctxMenu, open: false }); }}>Özellikler</MenuItem>,
          <MenuItem key="copy" onClick={() => { duplicateNode(ctxMenu.node); setCtxMenu({ ...ctxMenu, open: false }); }}>Kopyala</MenuItem>,
          <MenuItem key="delete" onClick={() => { deleteNode(ctxMenu.node); setCtxMenu({ ...ctxMenu, open: false }); }}>Sil</MenuItem>,
          ...(ctxMenu.node.type === 'circularNode' ? [
            <Divider key="divider" />,
            <MenuItem key="start" onClick={() => { setStartStop(ctxMenu.node, 'Start'); setCtxMenu({ ...ctxMenu, open: false }); }}>Start Olarak Ayarla</MenuItem>,
            <MenuItem key="stop" onClick={() => { setStartStop(ctxMenu.node, 'Stop'); setCtxMenu({ ...ctxMenu, open: false }); }}>Stop Olarak Ayarla</MenuItem>
          ] : [])
        ] : [
          <MenuItem key="fitview" onClick={() => { setCtxMenu({ ...ctxMenu, open: false }); reactFlowInstance?.fitView?.(); }}>Görünümü Sığdır</MenuItem>
        ]}
      </Menu>
      <Dialog open={propOpen} onClose={() => setPropOpen(false)} fullWidth maxWidth="md" key={selectedNode?.id}>
        <DialogTitle sx={{ backgroundColor: '#edbc45', color: '#000', fontWeight: 'bold' }}>
          Düğüm Özellikleri
          <IconButton onClick={() => setPropOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#000' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedNode && (
            <PanelWrapper key={selectedNode.id} nodeType={selectedNode.type} nodeId={selectedNode.id} data={selectedNode.data} onChange={(newData) => setNodes((nds) => nds.map(n => n.id === selectedNode.id ? { ...n, data: newData } : n))} />
          )}
        </DialogContent>
      </Dialog>
      {/* Toast Notification */}
      <Snackbar
        open={!!toastMessage}
        autoHideDuration={3000}
        onClose={() => dispatch(setToastMessage(null))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderTop: '6px solid #4caf50',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '4px',
            padding: '12px 16px',
            minWidth: '250px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ color: '#333', fontSize: '14px' }}>{toastMessage?.message}</span>
          <IconButton size="small" onClick={() => dispatch(setToastMessage(null))} sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
    </div>
  );
};

export default DnDFlow;
